import React from "react";
import { Swiper, SwiperSlide } from "swiper/react";
import { Autoplay, Pagination } from "swiper/modules";
import "swiper/css";
import "swiper/css/pagination";
import EN from "../../data/en.json";
import AR from "../../data/ar.json";

const DEFAULT_INDICES = [0, 1, 2, 4, 6, 7];

const GalleryCard = ({ item, isArabic, compact = false, imageFirst = false }) => {
  if (compact) {
    return (
      <div className="w-full overflow-hidden rounded-[2rem] border border-slate-700/50 bg-slate-800/40 hover:bg-slate-800/60 backdrop-blur shadow-xl group hover:-translate-y-1 hover:border-emerald-500/40 transition-all duration-300">
        <div className={`px-6 pt-6 pb-4 md:px-8 md:pt-8 md:pb-5 ${isArabic ? "text-right" : "text-left"}`}>
          <div className={`flex items-center gap-2.5 mb-3 ${isArabic ? "flex-row-reverse" : ""}`}>
            <div className="flex h-9 w-9 items-center justify-center rounded-xl bg-emerald-500/20 border border-emerald-500/30 flex-shrink-0">
              <img src={item.icon} alt="" className="h-4 w-4 object-contain brightness-0 invert" />
            </div>
            <span className="text-[10px] md:text-xs font-bold uppercase tracking-[0.2em] text-emerald-400">
              {isArabic ? "ميزة تشغيلية" : "Operational feature"}
            </span>
          </div>
          <h3 className="mb-2 text-lg md:text-xl 4k:text-2xl font-bold text-white group-hover:text-emerald-300 transition-colors leading-snug">{item.title}</h3>
          <p className="text-xs md:text-sm 4k:text-base leading-relaxed text-slate-400 mb-0 line-clamp-2">{item.paragraph}</p>
        </div>
        <div className="px-4 pb-4 md:px-6 md:pb-6">
          <div className="relative w-full overflow-hidden rounded-2xl border border-slate-700/40">
            <img
              src={item.img}
              alt={item.title}
              className="w-full h-auto object-cover object-top group-hover:scale-[1.02] transition-transform duration-700"
            />
          </div>
        </div>
      </div>
    );
  }

  return (
    <article className="overflow-hidden rounded-[2rem] border border-slate-200 bg-white shadow-xl h-full">
      <div className={`grid h-full ${imageFirst ? "lg:grid-cols-1" : "lg:grid-cols-[0.85fr_1.15fr]"}`}>
        <div className="bg-slate-900 p-5 md:p-6 flex items-center justify-center">
          <img
            src={item.img}
            alt={item.title}
            className="w-full rounded-[1.25rem] object-cover object-top shadow-2xl"
            style={{ minHeight: 220, maxHeight: imageFirst ? 320 : 360 }}
          />
        </div>
        <div className={`flex flex-col justify-center gap-3 p-5 md:p-6 ${isArabic ? "text-right" : "text-left"}`}>
          <div className={`flex items-center gap-3 ${isArabic ? "flex-row-reverse" : ""}`}>
            <div className="flex h-12 w-12 items-center justify-center rounded-2xl bg-emerald-50">
              <img src={item.icon} alt="" className="h-6 w-6 object-contain" />
            </div>
            <p className="mb-0 text-xs font-bold uppercase tracking-[0.22em] text-emerald-700">
              {isArabic ? "ميزة تشغيلية" : "Operational feature"}
            </p>
          </div>
          <h3 className="mb-0 text-2xl font-bold text-slate-900">{item.title}</h3>
          <p className="mb-0 text-sm leading-relaxed text-slate-600">
            {item.paragraph}
          </p>
        </div>
      </div>
    </article>
  );
};

export const PlatformMediaGallery = ({
  isArabic,
  indices = DEFAULT_INDICES,
  compact = false,
  layout = "carousel",
  showIntro = true,
}) => {
  const techData = (isArabic ? AR : EN).Tech;
  const slides = indices
    .map((index) => techData.Cards[index])
    .filter(Boolean);

  return (
    <div className={compact ? "" : "space-y-5"}>
      {!compact && showIntro ? (
        <div className={`${isArabic ? "text-right" : "text-left"}`}>
          <p className="mb-2 text-xs font-bold uppercase tracking-[0.24em] text-emerald-400">
            {isArabic ? "لقطات حقيقية من المنتج" : "Real platform surfaces"}
          </p>
          <p className="mb-0 max-w-3xl text-sm leading-relaxed text-slate-400 md:text-base">
            {isArabic
              ? "هذه اللقطات مأخوذة من نفس موارد المنصة المستخدمة في صفحة المنصة الأساسية حتى لا يضيع تموضع المنتج داخل الصفحة الرئيسية."
              : "These screenshots come from the same platform resources used on the product landing page, so the product story stays front and center."}
          </p>
        </div>
      ) : null}

      {layout === "grid" ? (
        <div className="grid gap-6 lg:grid-cols-2">
          {slides.map((item) => (
            <GalleryCard
              key={item.title}
              item={item}
              isArabic={isArabic}
              imageFirst={true}
            />
          ))}
        </div>
      ) : (
        <Swiper
          modules={[Autoplay, Pagination]}
          autoplay={{ delay: compact ? 5000 : 4200, disableOnInteraction: false }}
          pagination={{ clickable: true }}
          loop={true}
          spaceBetween={24}
          breakpoints={{
            0: { slidesPerView: 1 },
            768: { slidesPerView: compact ? 1.2 : 1.2 },
            1024: { slidesPerView: compact ? 1.5 : 1.5 },
            1440: { slidesPerView: compact ? 1.8 : 1.5 },
            2000: { slidesPerView: compact ? 2.2 : 2 },
            2560: { slidesPerView: compact ? 2.5 : 2.5 },
            3840: { slidesPerView: compact ? 3 : 3 },
          }}
          className={compact ? "pb-6" : "pb-12"}
        >
          {slides.map((item) => (
            <SwiperSlide key={item.title} className="!h-auto flex">
              <GalleryCard item={item} isArabic={isArabic} compact={compact} />
            </SwiperSlide>
          ))}
        </Swiper>
      )}
    </div>
  );
};

export default PlatformMediaGallery;